import React, { useEffect } from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import {
  EventRetentionRule,
  FileRetention,
  RetentionResourceType,
  RetentionRuleType,
  RoomRetention,
  SessionRetention,
  TTLRetentionRule,
} from '../../../models/RetentionPolicy'
import { useCreateRetentionPolicyMutation, useLazyGetRetentionPolicyQuery, useUpdateRetentionPolicyMutation } from '../../../store/account/account'
import { Modal } from '../../../components/Modal/Modal'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { SelectDropdown } from '../../../components/SelectDropdown/SelectDropdown'
import { FormFeedback } from '../../../components/Form/FormFeedback'
import { Tooltip } from '../../../components/Tooltip/Tooltip'

export interface EditPolicyModalProps {
  policyId?: string
  show: boolean
  onHide: () => void
}

type FormValues = {
  rule: TTLRetentionRule | EventRetentionRule
  resource: RoomRetention | SessionRetention | FileRetention
}
export const CreateOrEditPolicyModal: React.FC<EditPolicyModalProps> = ({ policyId, show, onHide }) => {
  const accountId = useAppSelector(selectAccountId)

  const defaultValues = {
    rule: {
      type: RetentionRuleType.TTL,
      config: {
        value: 30,
        unit: 'day',
      },
    },
    resource: {
      type: RetentionResourceType.SESSION,
      config: null,
    },
  }

  const [savePolicyById, { isLoading: isUpdating, isSuccess }] = useUpdateRetentionPolicyMutation()
  const [createPolicy, { isLoading: isCreating, isSuccess: isCreated }] = useCreateRetentionPolicyMutation()
  const getRuleInHours = (rule: TTLRetentionRule | EventRetentionRule): TTLRetentionRule | EventRetentionRule => {
    if (rule.type === RetentionRuleType.EVENT) {
      return rule
    }
    rule = rule as TTLRetentionRule
    if (rule.config.unit === 'day') {
      return { ...rule, config: { unit: 'hour', value: 24 * rule.config.value } } as TTLRetentionRule
    }

    if (rule.config.unit === 'week') {
      return { ...rule, config: { unit: 'hour', value: 7 * rule.config.value * 24 } } as TTLRetentionRule
    }
    return rule
  }
  const handleSave = async (data: FormValues) => {
    try {
      if (policyId) {
        await savePolicyById({
          accountId,
          policyId,
          body: {
            rule: getRuleInHours(data.rule),
            resource: data.resource,
          },
        }).unwrap()
      } else {
        await createPolicy({
          accountId,
          body: {
            rule: getRuleInHours(data.rule),
            resource: data.resource,
          },
        }).unwrap()
        form.reset()
      }
    } catch (e) {}
  }

  useEffect(() => {
    if ((policyId && isSuccess) || (!policyId && isCreated)) {
      onHide()
    }
  }, [isSuccess, isCreated])

  const form = useForm<FormValues>({ defaultValues })
  const { handleSubmit } = form

  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>{policyId ? 'Edit' : 'Create'} Data Retention Policy</h4>
        </Col>
      </Row>
      <Row>
        <FormProvider {...form}>
          <EditPolicyModalForm policyId={policyId} show={show} onHide={onHide} />
        </FormProvider>
      </Row>
    </Container>
  )

  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<></>}
      body={body}
      showFooterActions={true}
      onFooterPrimaryAction={handleSubmit(handleSave)}
      footerPrimaryActionLoading={policyId ? isUpdating : isCreating}
      centered={true}
    />
  )
}

const EditPolicyModalForm: React.FC<EditPolicyModalProps> = ({ policyId }) => {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<FormValues>()
  const resource = watch('resource')
  const accountId = useAppSelector(selectAccountId)
  const ruleTypes = [{ id: 'ttl', label: 'Time-to-live' }]
  const ttlRuleUnits = [
    { id: 'hour', label: 'Hour(s)' },
    { id: 'day', label: 'Day(s)' },
    { id: 'week', label: 'Weeks(s)' },
  ]
  const resourceTypes = [
    { id: 'file', label: 'File' },
    { id: 'session', label: 'Meeting' },
  ]
  const fileKinds = [
    { id: 'attachment', label: 'Attachment' },
    { id: 'recording', label: 'Meeting' },
    { id: 'transcription', label: 'Transcription' },
  ]

  const [getRetentionPolicy, getRetentionPolicyResult] = useLazyGetRetentionPolicyQuery()

  useEffect(() => {
    if (policyId) {
      getRetentionPolicy({ accountId, policyId })
    }
  }, [policyId])

  const resetResourceConfig = () => {
    setValue('resource.config.kind', null)
    setValue('resource.config.source', null)
  }

  useEffect(() => {
    if (getRetentionPolicyResult?.data && getRetentionPolicyResult.isSuccess) {
      const { data: retentionPolicy } = getRetentionPolicyResult.data
      setValue('rule.type', retentionPolicy.rule.type ?? '')
      setValue('resource.type', retentionPolicy.resource.type ?? '')
      if (retentionPolicy.rule.type === RetentionRuleType.TTL) {
        setValue('rule.config.unit', (retentionPolicy.rule as TTLRetentionRule).config.unit ?? null)
        setValue('rule.config.value', (retentionPolicy.rule as TTLRetentionRule).config.value ?? null)
      }
      if (retentionPolicy.resource.type === RetentionResourceType.FILE) {
        setValue('resource.config.kind', (retentionPolicy.resource as FileRetention).config?.kind ?? null)
      } else {
        resetResourceConfig()
      }
    }
  }, [getRetentionPolicyResult])

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      {getRetentionPolicyResult.isFetching ? (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      ) : (
        <>
          <Row>
            <Col>
              <Controller
                name='rule.type'
                control={control}
                rules={{
                  required: 'Rule type is required.',
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>
                      Retention Unit*
                      <Tooltip
                        className='zocks-text-gray-500'
                        placement='right'
                        title='⏳ Time-to-Live (TTL) is like an expiration date for your information. It defines how long we store your data in our systems. Once the TTL expires, your data is automatically deleted.️'
                      >
                        <i className='bi bi-info-circle' />
                      </Tooltip>
                    </Form.Label>
                    <SelectDropdown
                      className='zocks-px-0.75 py-2'
                      options={ruleTypes}
                      initialSelectedOption={ruleTypes.find((option) => option.id === field.value)}
                      onOptionSelected={(option) => field.onChange(option.id)}
                    />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <Form.Label>
                TTL Value*
                <Tooltip
                  className='zocks-text-gray-500'
                  placement='right'
                  title='⏳ Time-to-Live Value (TTL Value) is the duration your information stays in our system. After this period, your information is automatically removed.'
                >
                  <i className='bi bi-info-circle' />
                </Tooltip>
              </Form.Label>
            </Col>
            <Col>
              <Controller
                name='rule.config.value'
                control={control}
                rules={{}}
                render={({ field }) => (
                  <>
                    <Form.Control type='number' {...field} min={1} />
                  </>
                )}
              />
            </Col>
            <Col>
              <Controller
                name='rule.config.unit'
                control={control}
                rules={{}}
                render={({ field }) => (
                  <>
                    <SelectDropdown
                      className='zocks-px-0.75 py-2'
                      options={ttlRuleUnits}
                      initialSelectedOption={ttlRuleUnits.find((option) => option.id === field.value)}
                      onOptionSelected={(option) => field.onChange(option.id)}
                    />
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='resource.type'
                control={control}
                rules={{
                  required: 'Resource type is required.',
                }}
                render={({ field }) => (
                  <>
                    <Form.Label>Resource Type*</Form.Label>
                    <SelectDropdown
                      className='zocks-px-0.75 py-2'
                      options={resourceTypes}
                      initialSelectedOption={resourceTypes.find((option) => option.id === field.value)}
                      onOptionSelected={(option) => {
                        if (option.id === RetentionResourceType.SESSION) {
                          resetResourceConfig()
                        } else {
                          setValue('resource.config.kind', fileKinds[0].id)
                        }
                        field.onChange(option.id)
                      }}
                    />
                  </>
                )}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              {resource.type === 'file' && (
                <div className='field'>
                  <span className='p-float-label'>
                    <Controller
                      name='resource.config.kind'
                      control={control}
                      rules={{}}
                      render={({ field }) => (
                        <>
                          <Form.Label>Resource Type*</Form.Label>
                          <SelectDropdown
                            className='zocks-px-0.75 py-2'
                            options={fileKinds}
                            initialSelectedOption={fileKinds.find((option) => option.id === field.value)}
                            onOptionSelected={(option) => field.onChange(option.id)}
                          />
                        </>
                      )}
                    />
                  </span>
                </div>
              )}
            </Col>
          </Row>
        </>
      )}
    </Container>
  )
}
